import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {UpdateUserModel, UserModel, UserNotificationSettings, UserRole} from "../../../../types/control"
import {onClickHandler} from "../../../../utils/onclick"
import {useRPC} from "../../../../hooks/rpc"
import {useSelector} from "react-redux"
import {useKeyboard} from "../../../../hooks/keyboard"
import {hasAuthority} from "../../../../utils/role"
import {SelectItem} from "../../../../components/Select"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {useHistory, useLocation, useParams} from "react-router-dom"
import {useMessage} from "../../../../hooks/message"
import {useQuestion} from "../../../../hooks/question"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {SettingSelectComponent} from "../../../../components/Settings/SettingSelectComponent"
import {SettingReadOnlyRowComponent} from "../../../../components/Settings/SettingReadOnlyRowComponent"
import {useLocalization} from "../../../../hooks/localization"
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent"
import {isValidTime} from "../../../../utils/validation"
import {UserAgreementScreen} from "../../../ResetPasswordScreen"
import "./index.scss"

const BACK_URL = "/settings/users"

type Props = {
    onClick: (approved: boolean) => void
    name: string
    allowed: boolean
}

const convertDateToTimestamp = (date: string, time: string) => new Date(date + " " + time).getTime() / 1000
const convertBackDateTime = (timestamp: number) => {
    const newDataObject = new Date(timestamp * 1000)
        .toLocaleString("tr-TR")
        .slice(0, 16)
        .replace(/\./, "-")
        .replace(/\./, "-")
    return {
        date: newDataObject?.slice(0, 10)?.split("-")?.reverse()?.join("-"),
        time: newDataObject?.slice(11, 16)
    }
}

const NotificationSetting = memo(function NotificationSetting({name, allowed, onClick}: Props) {
    return (
        <div className="line">
            <span>{name}</span>
            <div className="half">
                <div className="half" {...onClickHandler(() => onClick(true))}>
                    <div className={"check-box" + (allowed ? " checked" : "")} />
                </div>
                <div className="half" {...onClickHandler(() => onClick(false))}>
                    <div className={"check-box" + (allowed ? "" : " checked")} />
                </div>
            </div>
        </div>
    )
})

const currentTimeStamp = Math.floor(new Date().getTime() / 1000)

export const UserEditScreen = memo(function UserEditScreen() {
    const {t} = useLocalization()
    const params = useParams<{id: string}>()
    const history = useHistory()
    const {success, failure} = useMessage()
    const question = useQuestion()
    const [rpc] = useRPC()
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard()
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard("numpad", true)
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] = useKeyboard(
        "numpad-special-extended",
        true
    )

    const userList = useSelector(state => state.control.userList)
    const authorityList = useSelector(state => state.control.authorityGroupList)
    const currentUsersRole = useSelector(state => state.ui.role)

    const ROLE_SELECTION_ITEMS = useMemo<SelectItem<UserRole>[]>(
        () => [
            {value: "user", label: t("user")},
            {value: "admin", label: t("admin")},
            {value: "service", label: t("service")}
            /*{value: 'developer', label: t('developer')},*/
        ],
        [t]
    )

    const options = useMemo<SelectItem<UserRole>[]>(
        () => ROLE_SELECTION_ITEMS.filter(m => hasAuthority(currentUsersRole, m.value)),
        [ROLE_SELECTION_ITEMS, currentUsersRole]
    )

    const authorityOptionList = useMemo<SelectItem<string>[]>(() => {
        const data = [{value: "", label: t("authority.group_none")}]
        for (const values of authorityList) {
            data.push({value: values.id, label: values.name})
        }
        return data
    }, [t, authorityList])

    const [selectedUser, setSelectedUser] = useState<UserModel>(null)
    const [isCloudUser, setIscloudUser] = useState(false);
    const [name, setName] = useState("")
    const [phoneNumber, setPhoneNumber] = useState("")
    const [verifyCode, setVerifyCode] = useState("")
    const [hasVerifyBeenSent, setHasVerifyBeenSent] = useState(false)

    const [unregisterOtpSent, setUnregisterOtpSent] = useState(false)
    const [unregisterOtpCode, setUnregisterOtpCode] = useState("")

    const [role, setRole] = useState<SelectItem<UserRole>>(options[0])
    const [authority, setAuthority] = useState<SelectItem<string>>(
        selectedUser == null ? authorityOptionList[0] : authorityOptionList.find(r => r.value)
    )
    const [accountPassword, setAccountPassword] = useState("")
    const [alarmPassword, setAlarmPassword] = useState("")
    const [notificationSettings, setNotificationSettings] = useState<UserNotificationSettings>({
        alarm: true,
        intercom: true,
        bell: true,
        intertext: true,
        security: true,
        aitips: true
    })

    const [isTemporaryUser, setIsTemporaryUser] = useState<boolean>(false)
    const [startDate, setStartDate] = useState<string>(convertBackDateTime(currentTimeStamp)?.date || "")
    const [startTime, setStartTime] = useState<string>(convertBackDateTime(currentTimeStamp)?.time || "")

    const [endDate, setEndDate] = useState<string>("")
    const [endTime, setEndTime] = useState<string>("23:59")

    const toggleNotificationSelected = useCallback((name: string, allowed: boolean) => {
        setNotificationSettings(notificationSettings => ({...notificationSettings, [name]: allowed}))
    }, [])

    const getValidationErrorMessage = useCallback(() => {
        if (!name || name.length < 3 || name === "default") {
            return t("pls_enter_a_valid_username")
        }

        if ((!selectedUser && !accountPassword) || (accountPassword && accountPassword.length !== 6)) {
            return t("login_password_must_be_6_digits")
        }

        if ((!selectedUser && !alarmPassword) || (alarmPassword && alarmPassword.length !== 4)) {
            return t("alar_password_must_be_4_digits")
        }

        if (isTemporaryUser && !selectedUser?.temporary_user?.is_inactive) {
            if (startDate === "" || endDate === "") {
                return t("pls_enter_a_valid_date")
            }
            const pickedStartingDate = new Date(startDate)
            if (!(pickedStartingDate instanceof Date) || isNaN(pickedStartingDate as unknown as number)) {
                return t("pls_enter_a_valid_date")
            }

            const pickedEndingDate = new Date(endDate)
            if (!(pickedEndingDate instanceof Date) || isNaN(pickedEndingDate as unknown as number)) {
                return t("pls_enter_a_valid_date")
            }

            if (!isValidTime(startTime) || !isValidTime(endTime)) {
                return t("pls_enter_a_valid_time")
            }

            if (Math.floor(Date.now() / 1000) >= convertDateToTimestamp(startDate, startTime)) {
                return t("pls_enter_a_valid_time")
            }

            if (convertDateToTimestamp(startDate, startTime) >= convertDateToTimestamp(endDate, endTime)) {
                return t("pls_enter_a_valid_time")
            }
        }

        return null
    }, [t, accountPassword, alarmPassword, name, selectedUser, isTemporaryUser, startDate, startTime, endDate, endTime])

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL)
    }, [history])

    const deletePressed = useCallback(() => {
        question(t("are_you_sure_you_want_to_delete"), () => {
            return rpc
                .deleteUser(selectedUser.id)
                .then(() => {
                    success(t("user_successfully_deleted"))
                    onBackPressed()
                })
                .catch(failure)
        })
    }, [t, failure, onBackPressed, question, rpc, selectedUser, success])

    const createPressed = useCallback((status?: boolean) => {
        const message = getValidationErrorMessage()
        if (message) {
            failure(message)

            return
        }

        const user: UpdateUserModel = {
            ...(selectedUser ? selectedUser : {id: null}),
            role: role.value,
            username: name,
            notification_settings: notificationSettings,
            password: accountPassword,
            alarm_password: alarmPassword,
            phone_number: phoneNumber,
            authority_group_id: role.value === "user" ? authority?.value : "",
            phone_number_verified: status || hasVerifyBeenSent,
            temporary_user:
                role.value === "user"
                    ? {
                          is_temporary_user: isTemporaryUser,
                          is_inactive: selectedUser?.temporary_user?.is_inactive || false,
                          start_timestamp: convertDateToTimestamp(startDate, startTime),
                          end_timestamp: convertDateToTimestamp(endDate, endTime)
                      }
                    : null
        }

        return rpc
            .updateUser(user)
            .then(() => {
                success(t(user.id != null ? "user_successfully_edited" : "user_successfully_created"))
                onBackPressed()
            })
            .catch(failure)
    }, [
        t,
        getValidationErrorMessage,
        selectedUser,
        role,
        authority,
        name,
        phoneNumber,
        notificationSettings,
        accountPassword,
        alarmPassword,
        rpc,
        isTemporaryUser,
        startDate,
        startTime,
        endDate,
        endTime,
        hasVerifyBeenSent,
        failure,
        success,
        onBackPressed
    ])

    const onFocusPassword = useCallback(() => {
        if (accountPassword === "******") {
            setAccountPassword("")
        }
    }, [accountPassword])

    const onFocusAlarmPassword = useCallback(() => {
        if (alarmPassword === "****") {
            setAlarmPassword("")
        }
    }, [alarmPassword])

    const onBlur = useCallback(() => {
        setActiveInput(null)
        setActiveNumpadInput(null)
        setActiveNumpadExtendedInput(null)
    }, [setActiveInput, setActiveNumpadInput, setActiveNumpadExtendedInput])

    const sendVerifyCode = useCallback(() => {
        if (!phoneNumber || phoneNumber.length < 10) {
            failure(t("pls_enter_a_valid_phone_number"))
            return
        }
        setHasVerifyBeenSent(true)

        rpc.sendCodeUserNumber(phoneNumber).catch(failure)
    }, [t, failure, rpc, phoneNumber])

    const approveCode = useCallback(() => {
        if (!verifyCode || verifyCode.length < 1) {
            failure(t("pls_enter_a_valid_code"))
            return
        }
        rpc.validateCode({user_id: selectedUser?.id, code: verifyCode})
            .then(() => {
                createPressed(true)
            })
            .catch(err => {
                failure(err)
                rpc.frontendErrorLog("Code validation error:" + err)
            })
    }, [t, failure, rpc, selectedUser, verifyCode, createPressed])

    const sendUnregisterOTP = useCallback(() => {
        if (!phoneNumber || phoneNumber.length < 10) {
            failure(t("pls_enter_a_valid_phone_number"))
            return
        }

        rpc.heartBeatSendOTP(phoneNumber)
            .then(() => {
                setUnregisterOtpSent(true)
            })
            .catch(err => {
                failure(err)
                rpc.frontendErrorLog("Unregister OTP send error:" + err)
            })
    }, [phoneNumber, rpc, failure, t])

    const unregisterPhoneNumber = useCallback(() => {
        if (!unregisterOtpCode || unregisterOtpCode.length < 1) {
            failure(t("pls_enter_a_valid_code"))
            return
        }

        rpc.heartBeatUnregister({phone_number: phoneNumber, code: unregisterOtpCode, user_id: selectedUser.id})
            .then(() => {
                success(t("phone_number_unregistered_successfully"))
                setPhoneNumber("")
                setHasVerifyBeenSent(false)
                setUnregisterOtpSent(false)
                setUnregisterOtpCode("")
                const updatedUser = {...selectedUser, phone_number: "", phone_number_verified: false}
                setSelectedUser(updatedUser)
            })
            .catch(err => {
                failure(err)
                rpc.frontendErrorLog("Unregister phone number error:" + err)
            })
    }, [unregisterOtpCode, selectedUser, rpc, failure, success, t, phoneNumber])

    const buttons = useMemo(
        () => [
            {
                text: t("delete"),
                className: "mulberry-wood",
                onClick: deletePressed,
                hidden: !selectedUser
            },
            {
                text: t("save"),
                className: "green",
                onClick: createPressed
            },
            {
                text: t("cancel"),
                className: "red",
                onClick: onBackPressed
            }
        ],
        [t, deletePressed, selectedUser, createPressed, onBackPressed]
    )

    const [openUserAgreement, setOpenUserAgreement] = useState(false)

    useEffect(() => {
        if (params.id == null) {
            return
        }

        const id = params.id
        const user = userList.find(user => user.id === id)
        if (user == null) {
            return
        }

        const convertedStartDate = convertBackDateTime(user?.temporary_user?.start_timestamp)
        const convertedEndDate = convertBackDateTime(user?.temporary_user?.end_timestamp)

        setSelectedUser(user)
        setIscloudUser(user.is_cloud_user);
        setName(user.username)
        setAccountPassword("******")
        setAlarmPassword("****")
        setRole(options.find(option => option.value === user.role))
        setAuthority(authorityOptionList.find(option => option.value === user.authority_group_id))
        setNotificationSettings(user.notification_settings)
        setPhoneNumber(user.phone_number)
        setIsTemporaryUser(user?.temporary_user?.is_temporary_user)
        setHasVerifyBeenSent(user?.phone_number_verified)
        if (user?.temporary_user?.is_temporary_user) {
            setStartDate(convertedStartDate?.date)
            setStartTime(convertedStartDate?.time)
            setEndDate(convertedEndDate?.date)
            setEndTime(convertedEndDate?.time)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <PasswordContainer requiredRole="user" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}
            >
                {openUserAgreement && (
                    <UserAgreementScreen isReadOnly={true} accepted={() => setOpenUserAgreement(false)} />
                )}
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={t("write_username")}
                            label={t("username")}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                            disabled={isCloudUser}
                        />
                        <SettingInputComponent
                            maxLength={6}
                            placeholder={t("write_login_password")}
                            label={t("login_password")}
                            value={accountPassword}
                            type="number-password"
                            setValue={setAccountPassword}
                            setActiveInput={setActiveNumpadInput}
                            onFocus={onFocusPassword}
                            onBlur={onBlur}
                            disabled={isCloudUser}
                        />
                        <SettingInputComponent
                            maxLength={4}
                            placeholder={t("write_alarm_password")}
                            label={t("alarm_password")}
                            type="number-password"
                            value={alarmPassword}
                            setValue={setAlarmPassword}
                            setActiveInput={setActiveNumpadInput}
                            onFocus={onFocusAlarmPassword}
                            onBlur={onBlur}
                            disabled={isCloudUser}
                        />
                        <SettingSelectComponent
                            label={t("authorization_level")}
                            options={options}
                            onChange={setRole}
                            value={role}
                            isDisabled={isCloudUser}
                        />
                        {renderIf(selectedUser)(() => (
                            <>
                                <SettingReadOnlyRowComponent title="Mobil OS" value={selectedUser.mobile_os} />
                                <SettingReadOnlyRowComponent title="Push Token" value={selectedUser.push_token} />
                            </>
                        ))}
                        {renderIf(!selectedUser?.phone_number_verified)(
                            () => (
                                <>
                                    <div className="phone-verify">
                                        <SettingInputComponent
                                            maxLength={12}
                                            placeholder={t("phone_number")}
                                            label={t("phone_number")}
                                            value={phoneNumber}
                                            setValue={setPhoneNumber}
                                            setActiveInput={setActiveNumpadInput}
                                            onBlur={onBlur}
                                        />
                                        {selectedUser != null && selectedUser.phone_number && (
                                            <button onClick={sendVerifyCode} disabled={hasVerifyBeenSent}>
                                                {t("verify")}
                                            </button>
                                        )}
                                    </div>

                                    {hasVerifyBeenSent && (
                                        <div className="phone-verify">
                                            <SettingInputComponent
                                                maxLength={12}
                                                placeholder={t("verify_code")}
                                                label={t("verify_code")}
                                                value={verifyCode}
                                                setValue={setVerifyCode}
                                                setActiveInput={setActiveInput}
                                                onBlur={onBlur}
                                            />
                                            <button onClick={approveCode}>{t("verify")}</button>
                                        </div>
                                    )}
                                </>
                            ),
                            () => (
                                <>
                                    <div className="phone-verify">
                                        <SettingReadOnlyRowComponent title={t("phone_number")} value={phoneNumber} />
                                        {!unregisterOtpSent && (
                                            <button onClick={sendUnregisterOTP} disabled={unregisterOtpSent}>
                                                {t("delete_number")}
                                            </button>
                                        )}
                                    </div>

                                    {unregisterOtpSent && (
                                        <div className="phone-verify">
                                            <SettingInputComponent
                                                maxLength={12}
                                                placeholder={t("verify_code")}
                                                label={t("verify_code")}
                                                value={unregisterOtpCode}
                                                setValue={setUnregisterOtpCode}
                                                setActiveInput={setActiveInput}
                                                onBlur={onBlur}
                                            />
                                            <button onClick={unregisterPhoneNumber}>{t("approve_delete")}</button>
                                        </div>
                                    )}
                                </>
                            )
                        )}

                        {renderIf(currentUsersRole === "user" || role.value !== "user")(
                            () => (
                                <SettingReadOnlyRowComponent
                                    title={t("authority.auth_group_name")}
                                    value={authority.label}
                                />
                            ),
                            () => (
                                <SettingSelectComponent
                                    label={t("authority.auth_group_name")}
                                    options={authorityOptionList}
                                    onChange={setAuthority}
                                    value={authority}
                                />
                            )
                        )}
                        {renderIf(role.value === "user" && currentUsersRole !== "user")(
                            () => (
                                <>
                                    <SettingCheckBoxComponent
                                        label={t("temporary_user")}
                                        set={() => setIsTemporaryUser(e => !e)}
                                        value={isTemporaryUser}
                                    />
                                    {renderIf(isTemporaryUser)(() => (
                                        <>
                                            <SettingInputComponent
                                                placeholder={t("starting_date")}
                                                label={t("starting_date")}
                                                value={startDate}
                                                setValue={setStartDate}
                                                setActiveInput={null}
                                                type="date"
                                            />
                                            <SettingInputComponent
                                                maxLength={5}
                                                placeholder={t("starting_time")}
                                                label={t("starting_time")}
                                                value={startTime}
                                                setValue={setStartTime}
                                                setActiveInput={setActiveNumpadExtendedInput}
                                                onBlur={onBlur}
                                            />
                                            <SettingInputComponent
                                                placeholder={t("ending_date")}
                                                label={t("ending_date")}
                                                value={endDate}
                                                setValue={setEndDate}
                                                setActiveInput={null}
                                                type="date"
                                            />
                                            <SettingInputComponent
                                                maxLength={5}
                                                placeholder={t("ending_time")}
                                                label={t("ending_time")}
                                                value={endTime}
                                                setValue={setEndTime}
                                                setActiveInput={setActiveNumpadExtendedInput}
                                                onBlur={onBlur}
                                            />
                                        </>
                                    ))}
                                </>
                            ),
                            () =>
                                renderIf(isTemporaryUser)(() => (
                                    <>
                                        <SettingReadOnlyRowComponent
                                            title={t("starting_date")}
                                            value={startDate + " " + startTime}
                                        />
                                        <SettingReadOnlyRowComponent
                                            title={t("ending_date")}
                                            value={endDate + " " + endTime}
                                        />
                                    </>
                                ))
                        )}
                    </div>
                    <div className="content-wrapper">
                        <div className="table-title">
                            <div className="half">
                                <div className="flex">
                                    <span>{t("notification_settings")}</span>
                                </div>
                            </div>
                            <div className="half">
                                <div className="half user-settings-overflow">
                                    <span>{t("allow")}</span>
                                </div>
                                <div className="half user-settings-overflow">
                                    <span>{t("do_not_allow")}</span>
                                </div>
                            </div>
                        </div>
                        <div className="notification-list">
                            <NotificationSetting
                                name={t("alarm")}
                                allowed={notificationSettings.alarm}
                                onClick={isApproved => toggleNotificationSelected("alarm", isApproved)}
                            />
                            <NotificationSetting
                                name={t("intercom")}
                                allowed={notificationSettings.intercom}
                                onClick={isApproved => toggleNotificationSelected("intercom", isApproved)}
                            />
                            <NotificationSetting
                                name={t("bell")}
                                allowed={notificationSettings.bell}
                                onClick={isApproved => toggleNotificationSelected("bell", isApproved)}
                            />
                            <NotificationSetting
                                name={t("inter_text")}
                                allowed={notificationSettings.intertext}
                                onClick={isApproved => toggleNotificationSelected("intertext", isApproved)}
                            />
                            <NotificationSetting
                                name={t("system_security")}
                                allowed={notificationSettings.security}
                                onClick={isApproved => toggleNotificationSelected("security", isApproved)}
                            />
                            <NotificationSetting
                                name={t("artificial_intelligence_suggestions")}
                                allowed={notificationSettings.aitips}
                                onClick={isApproved => toggleNotificationSelected("aitips", isApproved)}
                            />
                        </div>
                        {renderIf(selectedUser && (selectedUser.role === "user" || selectedUser.role === "admin"))(
                            () => (
                                <>
                                    {renderIf(!selectedUser?.first_login)(() => (
                                        <div className="agreement-information">
                                            <div className="agreement-row">
                                                <span>{t("contracts_i_accepted")}</span>
                                                <div
                                                    className="show-agreements"
                                                    {...onClickHandler(() => setOpenUserAgreement(true))}
                                                >
                                                    {t("show_it")}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )
                        )}
                    </div>
                </div>
            </SettingsPageContainer>
            {renderKeyboard()}
            {renderNumpadKeyboard()}
            {renderNumpadExtendedKeyboard()}
        </PasswordContainer>
    )
})
